/**
 * **The choice seam** — how a resolution asks a human a question and reads the recorded answer.
 * A `choose` atom, a reaction offer, and a `gm_ruling` predicate all read answers through a
 * {@link ChoiceSource} keyed by a stable prompt id, and all halt the same way: an interactive
 * source (`promptingChoices`) with no recorded answer throws {@link NeedsDecisionSignal}, which
 * the resolver's catch sites turn into `needs_decision`. Split out of `interpret.ts` so
 * `predicate.ts` can halt without importing the interpreter.
 */

import type { Cell } from "@lancer-vtt/domain";
import type { PromptAnswerKind } from "@lancer-vtt/shared";

/**
 * The recorded-choice answerer for a `choose` atom. Optional on the interpreter
 * context: when present, it returns the selected option id for a prompt (a recorded
 * player decision / a scripted fixture choice); the interpreter then runs that
 * option's effects. Absent → a `choose` resolves to its **first** option (the
 * faithful no-roundtrip default, like the auto-consume Lock On default in `resolve`).
 */
export type ChoiceSource = {
  /** The selected option id for `prompt` among `optionIds`, or undefined to take the default. */
  choose(prompt: string, optionIds: string[]): string | undefined;
  /**
   * The recorded answer to a multi-pick (`choose_from_area` `count: "any"`): the ids joined by `,`
   * read back as the subset of `optionIds` they name, in `optionIds` order (`""` is none), or
   * undefined when unanswered.
   */
  chooseMany?(prompt: string, optionIds: string[]): string[] | undefined;
  /**
   * Whether an *unanswered* `choose` must **halt** resolution and round-trip a prompt
   * to the player, rather than silently take the first
   * option. Optional — absent / `false` keeps the faithful no-roundtrip default (a
   * scripted fixture, a replay, the CLI's pre-recorded `--choice`). The server's
   * interactive path sets it `true` so a `choose` with no recorded answer becomes a
   * {@link NeedsDecisionSignal} the resolver converts to `needs_decision`.
   */
  readonly requireAnswer?: boolean;
}

/**
 * Thrown by `interpret.ts`'s `interpretChoose` (and by `predicate.ts`'s `gm_ruling`) when the {@link ChoiceSource} `requireAnswer`s a
 * decision the recorded `choices` didn't answer. It is
 * **control flow, not an error**: `resolveActivate`/`resolve` catch it and return a
 * `needs_decision` {@link import("../resolve").ResolveResult} instead of steps, so the
 * authority can round-trip the prompt to the player. It carries the atom's full
 * options (id + label) — the interpreter has them; the bare `ChoiceSource.choose` does
 * not — so the prompt the client sees is the authored one. Never escapes the resolver.
 *
 * **Also thrown by `interpret.ts`'s `interpretMoveSelfTowardTarget`** for a **spatial** decision — a
 * direction/cell answer, not a discrete option list. The
 * `answerKind` discriminator tells the resolver/client which shape to round-trip: `"options"`
 * (the default — a `choose` prompt, `options` populated) or `"direction"` (Ace's
 * Acrobatics — the client answers by picking a cell/direction on the map/CLI, not a listed
 * option; `options` is `[]`, unused).
 */
/** See {@link NeedsDecisionSignal.section}. */
export type DecisionSection = { index: number; count: number; size: number; placed: Cell[] };

export class NeedsDecisionSignal {
  /** The selectable options the client modal presents (`[]` for a `"direction"` decision; each carries its `cell` for a `"cell"` one). */
  readonly options: { id: string; label: string; cell?: Cell }[];
  /**
   * **Which actor must answer.** `undefined` for a same-actor prompt (the `choose` atom): the
   * resolver's catch site defaults it to the **acting actor**, so a variable-damage-type /
   * Invade / Lock On / Ram / Custom Paint Job prompt routes to the attacker. A **defender's
   * reaction** (Brace) sets it to the **target** — the cross-actor case this field unlocks.
   */
  readonly who?: string;
  /**
   * The decision's answer shape. `"options"` (the default, omitted `decision.answerKind`) is the
   * discrete `choose`/reaction-offer prompt; `"direction"` is a spatial pick (a cell/heading),
   * answered via the CLI's `--react-toward` or the browser's map-click mode, not a listed option;
   * `"combatant"` is a pick among the listed candidate combatants (`choose_from_area`), answered
   * like an option with the combatant id; `"cell"` is a pick among the listed map spaces (an area
   * deploy's site, a tied `place_adjacent` landing), each option carrying its `cell` and keyed by
   * it, answered like an option.
   */
  readonly answerKind: PromptAnswerKind;
  /**
   * **The paintable "where can this magnitude pick land" set** ({@link
   * import("./spatialAnswerer").travelReachableCells}) — display-only, mirrors
   * `resolve.ts`'s `NeedsDecision.incomingDamage` precedent (computed once at the throw site,
   * where the atom's `dist`/`towardCell`/`as` are in scope, then carried display-only the rest of
   * the way — the resolver's generic catch sites never re-derive it). Populated ONLY for the
   * fixed-direction magnitude pick (Hunter's Lunge shape); `undefined` for a `toward:"chosen"`
   * halt (no anchor exists yet to draw a ray toward) and for every non-spatial `choose` decision —
   * a client falls back to its bare-crosshair affordance whenever this is absent.
   */
  readonly reachableCells?: Cell[];
  /**
   * For a `"cell"` prompt that places one section of several (an area `deploy_object`): this is
   * section `index` (0-based) of `count`, each `size` spaces across, and `placed` holds the cells
   * the earlier sections were answered with this pass. A client ghosts `placed` and asks for a
   * confirm on the last section, so one confirm ends the placement. Display-only.
   */
  readonly section?: DecisionSection;
  /**
   * The decision's **short name** (`Brace`, `Overwatch`, `Intercede`) — set by the reaction
   * registries, absent for a plain `choose` atom. The COMMS card builds its question headline
   * from it ("Brace against Ronin's hit?") and the durable outcome line names the decision by it
   * (`<unit> · Brace: Brace`). Display-only, like `prompt`.
   */
  readonly title?: string;
  /**
   * An optional **one-line cost summary** (`comms-attack-reaction-cards` settled item 6) — what
   * the damage tiles can't show, e.g. Brace's next-turn penalty. Display-only; absent for every
   * decision that doesn't author one.
   */
  readonly summary?: string;
  constructor(
    /** The atom's stable prompt id (the key the answer records under). */
    readonly promptId: string,
    /** The authored prompt text (falls back to the id when the atom names none). */
    readonly prompt: string,
    /** The options + (optional) cross-actor `who`/`answerKind`/`reachableCells`/`title`/`summary` — bundled to keep the constructor ≤3 params. */
    decision: {
      options: { id: string; label: string; cell?: Cell }[];
      who?: string;
      answerKind?: PromptAnswerKind;
      reachableCells?: Cell[];
      section?: DecisionSection;
      title?: string;
      summary?: string;
    },
  ) {
    this.options = decision.options;
    this.who = decision.who;
    this.answerKind = decision.answerKind ?? "options";
    this.reachableCells = decision.reachableCells;
    if (decision.section !== undefined) this.section = decision.section;
    if (decision.title !== undefined) this.title = decision.title;
    if (decision.summary !== undefined) this.summary = decision.summary;
  }
}

/**
 * Build a {@link ChoiceSource} from a recorded `prompt → optionId` map — the `choose`
 * roundtrip transport. This is the injectable seam mirroring
 * the `DiceSource`/`InputSource` the resolver already records every roll through: a
 * `RequestAttack.choices`/`RequestActivate.choices` map (a player's recorded decision,
 * or a scripted fixture's) becomes a `ChoiceSource` the interpreter reads, so the taken
 * branch is deterministic + replayable. An answer not among the offered `optionIds` is
 * ignored (falls through to the default).
 */
export function recordedChoices(map: Record<string, string> | undefined): ChoiceSource | undefined {
  if (!map || Object.keys(map).length === 0) return undefined;
  return {
    choose(prompt, optionIds) {
      const picked = map[prompt];
      return picked !== undefined && optionIds.includes(picked) ? picked : undefined;
    },
    chooseMany: (prompt, optionIds) => pickedSubset(map[prompt], optionIds),
  };
}

/**
 * Build an **interactive** {@link ChoiceSource} — the server's prompt round-trip path.
 * It answers from the recorded `map` exactly like
 * {@link recordedChoices}, but sets `requireAnswer` so an *unanswered* `choose` halts
 * resolution with a {@link NeedsDecisionSignal} (which the resolver turns into
 * `needs_decision`) instead of silently taking the first option. This is the one
 * difference between "resolve interactively, prompting the human for missing choices"
 * (the live `/map` + `/encounter` path) and "resolve deterministically off recorded
 * choices" (replay, the CLI's `--choice`, a scripted fixture). Always returns a source
 * (even with an empty/absent map) — the whole point is to require an answer for any
 * `choose` the map doesn't cover.
 */
export function promptingChoices(map: Record<string, string> | undefined): ChoiceSource {
  return {
    requireAnswer: true,
    choose(prompt, optionIds) {
      const picked = map?.[prompt];
      return picked !== undefined && optionIds.includes(picked) ? picked : undefined;
    },
    chooseMany: (prompt, optionIds) => pickedSubset(map?.[prompt], optionIds),
  };
}

/** Recorded answers in either shape: an intent's `choices` map, or an effect context's {@link ChoiceSource}. */
export type RecordedAnswers = Record<string, string> | ChoiceSource;

/** A recorded-answer map holds only strings, so a `choose` function marks a {@link ChoiceSource}. */
export function isChoiceSource(answers: RecordedAnswers): answers is ChoiceSource {
  return typeof answers.choose === "function";
}

/** The recorded answer to `promptId` among `optionIds`, read from either shape; undefined when unanswered. */
export function recordedAnswer(answers: RecordedAnswers | undefined, promptId: string, optionIds: string[]): string | undefined {
  if (answers === undefined) return undefined;
  if (isChoiceSource(answers)) return answers.choose(promptId, optionIds);
  const picked = answers[promptId];
  return picked !== undefined && optionIds.includes(picked) ? picked : undefined;
}

/** A `,`-joined multi-pick answer as the named subset of `optionIds`, in their order; undefined when unanswered. */
function pickedSubset(answer: string | undefined, optionIds: string[]): string[] | undefined {
  if (answer === undefined) return undefined;
  const named = new Set(answer.split(",").filter((id) => id !== ""));
  return optionIds.filter((id) => named.has(id));
}
